import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  CONTACT_EMAIL,
  CONTACT_EMAIL_HREF,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  CONTACT_WHATSAPP_DISPLAY,
  getWhatsAppUrl,
} from "@/lib/contact";
import { API } from "@/lib/api";

export default function Contact() {
  const { t } = useTranslation();
  const c = (k) => t(`patientService.contact.${k}`);
  const f = (k) => t(`patientService.contact.form.${k}`);

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = f("required");
    if (!form.email.trim()) e.email = f("required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = f("invalidEmail");
    if (!form.subject.trim()) e.subject = f("required");
    if (!form.message.trim()) e.message = f("required");
    if (!consent) e.consent = f("consentRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(f("successTitle"), { description: f("successText") });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setConsent(false);
      setErrors({});
    } catch {
      toast.error(f("errorTitle"), { description: f("errorText") });
    } finally {
      setLoading(false);
    }
  };

  const whatsappHref = getWhatsAppUrl(t("common.whatsappMessage"));
  const infoRows = [
    { icon: MapPin, title: c("addressTitle"), value: c("address") },
    { icon: Phone, title: c("phoneTitle"), value: CONTACT_PHONE_DISPLAY, href: CONTACT_PHONE_HREF },
    { icon: Mail, title: c("emailTitle"), value: CONTACT_EMAIL, href: CONTACT_EMAIL_HREF },
    {
      icon: MessageCircle,
      title: c("whatsappTitle"),
      value: CONTACT_WHATSAPP_DISPLAY,
      href: whatsappHref,
      external: true,
      note: c("whatsappNote"),
    },
  ];

  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" />
      <PageHero kicker={c("kicker")} title={c("title")} text={c("text")} />

      <section className="bg-warm-ivory">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-24 lg:grid-cols-[1fr_1.2fr] lg:gap-16 lg:px-10">
          {/* Practice contact details */}
          <Reveal>
            <div className="space-y-6">
              <div className="rounded-3xl bg-sand p-7">
                <div className="space-y-5">
                  {infoRows.map((r, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warm-ivory text-champagne-gold">
                        <r.icon className="h-4 w-4" strokeWidth={1.7} />
                      </span>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-editorial text-deep-ink/50">{r.title}</p>
                        {r.href ? (
                          <a
                            href={r.href}
                            target={r.external ? "_blank" : undefined}
                            rel={r.external ? "noopener noreferrer" : undefined}
                            className="mt-0.5 inline-flex text-base text-deep-ink/85 transition-colors hover:text-champagne-gold"
                          >
                            {r.value}
                          </a>
                        ) : (
                          <p className="mt-0.5 text-base text-deep-ink/85">{r.value}</p>
                        )}
                        {r.note && <p className="mt-1 max-w-sm text-xs leading-relaxed text-deep-ink/55">{r.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border">
                <iframe
                  title="HAYAT location"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=31.44%2C30.00%2C31.49%2C30.03&layer=mapnik"
                  className="h-56 w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.08}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              noValidate
              className="rounded-3xl border border-border bg-warm-ivory p-7 lg:p-10"
            >
              <h2 className="font-serif text-3xl text-deep-ink">{c("formTitle")}</h2>
              <p className="mt-2 text-sm leading-relaxed text-deep-ink/60">{c("formIntro")}</p>

              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <Field label={f("name")} error={errors.name}>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} className="bg-sand" />
                </Field>
                <Field label={f("email")} error={errors.email}>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="bg-sand" />
                </Field>
                <Field label={f("phone")}>
                  <Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="bg-sand" />
                </Field>
                <Field label={f("subject")} error={errors.subject}>
                  <Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="bg-sand" />
                </Field>
              </div>
              <div className="mt-5">
                <Field label={f("message")} error={errors.message}>
                  <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={set("message")} className="bg-sand" />
                </Field>
              </div>

              <div className="mt-5 flex items-start gap-3">
                <Checkbox
                  id="consent"
                  data-testid="contact-consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-1"
                />
                <Label htmlFor="consent" className="text-sm leading-relaxed text-deep-ink/70">
                  {f("consent")}
                </Label>
              </div>
              {errors.consent && <p className="mt-1 text-sm text-terracotta">{errors.consent}</p>}

              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="mt-7 w-full rounded-full bg-deep-ink py-6 text-base text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink sm:w-auto sm:px-10"
              >
                {loading ? f("sending") : f("submit")}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm text-deep-ink/70">{label}</Label>
    {children}
    {error && <p className="mt-1 text-sm text-terracotta">{error}</p>}
  </div>
);
